
import React, { useState } from 'react';
import { Calendar, Target, Users, DollarSign, Clock, CheckCircle } from 'lucide-react';

const CreateCampaign = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    segments: [],
    minPurchase: '',
    timeConstraint: '',
  });
  
  const [previewData, setPreviewData] = useState({
    estimatedReach: '1.2M',
    estimatedImpact: '+4.3%',
    costEstimate: '3.2M ฿',
    roi: '2.1x'
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSegmentChange = (segment) => {
    const updatedSegments = formData.segments.includes(segment)
      ? formData.segments.filter(s => s !== segment)
      : [...formData.segments, segment];
    
    setFormData({
      ...formData,
      segments: updatedSegments
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // Campaign creation logic would go here
    alert('Campaign created successfully!');
  };
  
  // Segment options
  const segmentOptions = [
    { id: 'urban', name: 'Urban Premium', stations: 523, customers: '450K' },
    { id: 'highway', name: 'Highway Transit', stations: 682, customers: '320K' },
    { id: 'rural', name: 'Rural Standard', stations: 754, customers: '280K' },
    { id: 'suburban', name: 'Suburban Mixed', stations: 384, customers: '210K' },
  ];
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Create Campaign</h1>
        <p className="text-gray-600">Design a new marketing campaign with targeted parameters and projected impact.</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Section */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
            <div className="mb-6">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Enter campaign name"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Describe the campaign objective and mechanics"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Target Segments</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {segmentOptions.map(segment => (
                  <div 
                    key={segment.id}
                    onClick={() => handleSegmentChange(segment.id)}
                    className={`cursor-pointer border ${formData.segments.includes(segment.id) ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-white'} rounded-lg p-3 transition-colors`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-gray-900">{segment.name}</h4>
                        <p className="text-xs text-gray-500 mt-1">{segment.stations} stations · {segment.customers} customers</p>
                      </div>
                      {formData.segments.includes(segment.id) && (
                        <CheckCircle size={18} className="text-purple-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label htmlFor="minPurchase" className="block text-sm font-medium text-gray-700 mb-1">Minimum Purchase Value</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="minPurchase"
                    name="minPurchase"
                    value={formData.minPurchase}
                    onChange={handleChange}
                    className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g. 500 ฿"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="timeConstraint" className="block text-sm font-medium text-gray-700 mb-1">Time Constraints</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Clock size={16} className="text-gray-400" />
                  </div>
                  <select
                    id="timeConstraint"
                    name="timeConstraint"
                    value={formData.timeConstraint}
                    onChange={handleChange}
                    className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">No time constraint</option>
                    <option value="weekdays">Weekdays only</option>
                    <option value="weekends">Weekends only</option>
                    <option value="morning">Morning hours (6AM-10AM)</option>
                    <option value="evening">Evening hours (6PM-10PM)</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-4">
              <button type="button" className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50">
                Save Draft
              </button>
              <button type="submit" className="px-4 py-2 bg-purple-700 text-white rounded-md hover:bg-purple-800">
                Create Campaign
              </button>
            </div>
          </form>
        </div>
        
        {/* Preview Section */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Preview</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Estimated Reach</p>
                <div className="flex items-center">
                  <Users size={18} className="text-purple-600 mr-2" />
                  <span className="text-xl font-medium text-gray-900">{previewData.estimatedReach}</span>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Estimated Impact</p>
                <div className="flex items-center">
                  <Target size={18} className="text-purple-600 mr-2" />
                  <span className="text-xl font-medium text-gray-900">{previewData.estimatedImpact}</span>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Cost Estimate</p>
                <div className="flex items-center">
                  <DollarSign size={18} className="text-purple-600 mr-2" />
                  <span className="text-xl font-medium text-gray-900">{previewData.costEstimate}</span>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Projected ROI</p>
                <div className="flex items-center">
                  <TrendingUp size={18} className="text-purple-600 mr-2" />
                  <span className="text-xl font-medium text-gray-900">{previewData.roi}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
            <h3 className="text-md font-medium text-purple-900 mb-2">AI Recommendations</h3>
            <p className="text-sm text-gray-700 mb-3">
              Based on your selections, consider these optimizations:
            </p>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-start">
                <CheckCircle size={16} className="text-purple-600 mr-2 mt-0.5" />
                <span>Extend campaign to Rural Standard segment for +1.2% additional impact</span>
              </li>
              <li className="flex items-start">
                <CheckCircle size={16} className="text-purple-600 mr-2 mt-0.5" />
                <span>Café Amazon cross-promotion could increase ROI by 0.3x</span>
              </li>
              <li className="flex items-start">
                <CheckCircle size={16} className="text-purple-600 mr-2 mt-0.5" />
                <span>Optimal minimum purchase threshold is 400 ฿ based on segment data</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCampaign;
